import React from 'react';
import { Button, useToast } from '@chakra-ui/react';

/**
 * Call / Text buttons that drive the Dialerr softphone.
 *
 * Replaces the plain `tel:` / `sms:` links these buttons used to be. Those
 * handed the call off to whatever the admin's machine registered for the
 * protocol (their desk phone, FaceTime, Messages), so the call never went
 * through the business line, never got logged in Dialerr, and never produced a
 * disposition. Routing through the softphone keeps all of that in one place.
 *
 * Requires window.Dialerr, which DialerrWidget puts on the page — mounted by
 * AdminLayout.js:112 for any logged-in admin, so it's present anywhere in the
 * admin panel.
 *
 * Passing leadName into startCall is what makes the softphone show the
 * patient's name rather than "Unknown Lead". That matters even once
 * api/dialerr/sync-leads.js is pushing leads, because it also covers numbers
 * edited after the push, and rows the sweep skipped.
 */

/**
 * Raw phone text -> E.164, or null if it isn't dialable.
 * patient_phone is free-text: "(555) 123-4567", "5551234567", "+15551234567"
 * all occur. Keep in step with phoneDigits() in api/_dialerrOutcomes.js.
 */
function toE164(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length < 10) return null;
  // Same placeholder guard as normalizeLdxPhone in api/ldx-sync.js — LDX sends
  // 8888888888 / 0000000000 for "no phone".
  if (/^(\d)\1+$/.test(digits)) return null;
  return digits.length === 10 ? `+1${digits}` : `+${digits}`;
}

export default function DialerrActions({ phone, name, size = 'xs' }) {
  const toast = useToast();
  const e164 = toE164(phone);
  if (!e164) return null;

  const leadName = (name || '').trim() || 'Unknown patient';

  const notReady = () =>
    toast({
      title: 'Softphone not loaded',
      description:
        'The Dialerr widget is not on the page. Reload the admin panel; if it persists, check that REACT_APP_DIALERR_KEY was set at build time.',
      status: 'warning',
      duration: 6000,
      isClosable: true,
    });

  const handleCall = () => {
    if (typeof window.Dialerr?.startCall !== 'function') return notReady();
    // startCall opens the panel itself (embed.js:208).
    window.Dialerr.startCall(e164, leadName);
  };

  const handleText = () => {
    if (typeof window.Dialerr?.open !== 'function') return notReady();
    // embed.js can only send DIALERR_SET_TOKEN / DIALERR_START_CALL /
    // DIALERR_VISIBLE_HEIGHT to the toolbar. There is NO outbound-SMS command
    // (sms_received is receive-only), so this can only open the softphone for
    // the agent to send the text from its messaging tab. If Dialerr adds a
    // startText(phone, leadName), call it here and drop the title caveat.
    window.Dialerr.open();
  };

  return (
    <>
      <Button size={size} colorScheme="green" onClick={handleCall}>
        Call
      </Button>
      <Button
        size={size}
        colorScheme="blue"
        variant="outline"
        onClick={handleText}
        title="Opens the softphone — Dialerr has no send-text API"
      >
        Text
      </Button>
    </>
  );
}
